/**
 * RoomTravelersSelector Component
 *
 * Oda bazlı travelers seçimi için component.
 * Maksimum oda sayısı maxRooms ile (varsayılan 10); talep formunda 40 kullanılabilir.
 * Controlled mod: rooms + onRoomsChange verilirse context kullanılmaz (talep formu için).
 */

'use client'

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTravelersPreferences } from '@/contexts/TravelersPreferencesContext'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import type { Room } from '@/types/travelers'
import { validateRoomCapacity } from '@/types/travelers'

const DEFAULT_MAX_ROOMS = 10

export interface RoomTravelersSelectorProps {
  title?: string
  icon?: React.ReactNode
  departureDate?: Date | null // Çocuk yaşı validasyonu için
  /** İlk render'da dropdown açık olsun (örn. sidebar'dan tıklayınca tek tıkla açılması için) */
  defaultOpen?: boolean
  /** Maksimum oda sayısı (varsayılan 10; talep formu için 40) */
  maxRooms?: number
  /** Controlled mod: verilirse context yerine bu değer kullanılır (talep formu) */
  rooms?: Room[]
  /** Controlled mod: odalar değiştiğinde çağrılır */
  onRoomsChange?: (rooms: Room[]) => void
  /** true ise içerik dropdown değil, formla aynı bordered kutu içinde inline gösterilir (Book now modal) */
  inline?: boolean
  /** Modal için kompakt stil (diğer inputlarla aynı border/çerçeve) */
  compactModal?: boolean
}

export default function RoomTravelersSelector({
  title,
  icon,
  departureDate,
  defaultOpen = false,
  maxRooms = DEFAULT_MAX_ROOMS,
  rooms: controlledRooms,
  onRoomsChange,
  inline = false,
  compactModal = false,
}: RoomTravelersSelectorProps) {
  const context = useTravelersPreferences()
  const { getVariable } = useLanguageVariables()
  const [isOpen, setIsOpen] = useState(inline ? true : defaultOpen)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownContentRef = useRef<HTMLDivElement>(null)

  const isControlled = controlledRooms !== undefined && onRoomsChange !== undefined
  const rooms = isControlled ? controlledRooms : (context.preferences?.rooms ?? [])

  // Controlled mod: add/remove/update odaları parent state'e yansıt
  const addRoomControlled = useCallback(() => {
    if (rooms.length >= maxRooms) return
    onRoomsChange?.([...rooms, { adults: 2, children: 0, childAges: [] }])
  }, [rooms, maxRooms, onRoomsChange])
  const removeRoomControlled = useCallback((index: number) => {
    if (rooms.length <= 1) return
    onRoomsChange?.(rooms.filter((_, i) => i !== index))
  }, [rooms, onRoomsChange])
  const updateRoomControlled = useCallback((index: number, updatedRoom: Room) => {
    if (index < 0 || index >= rooms.length) return
    const next = [...rooms]
    next[index] = updatedRoom
    onRoomsChange?.(next)
  }, [rooms, onRoomsChange])

  const addRoom = isControlled ? addRoomControlled : context.addRoom
  const removeRoom = isControlled ? removeRoomControlled : context.removeRoom
  const updateRoom = isControlled ? updateRoomControlled : context.updateRoom

  // Dışarıya tıklandığında menüyü kapat
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Toplam kişi sayısını hesapla - useMemo ile optimize edilmiş
  const totalTravelers = useMemo(() => {
    if (!rooms.length) return 0
    return rooms.reduce((total, room) => total + room.adults + room.children, 0)
  }, [rooms])

  // Oda özeti metni - useMemo ile optimize edilmiş
  const summaryText = useMemo(() => {
    if (!rooms.length) return '02'
    const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0)
    const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0)
    const totalPeople = totalAdults + totalChildren
    return totalPeople.toString().padStart(2, '0')
  }, [rooms])

  // Oda güncelleme handler'ları
  const handleRoomUpdate = useCallback((index: number, updates: Partial<Room>) => {
    if (!rooms.length || index < 0 || index >= rooms.length) return

    const room = rooms[index]
    const updatedRoom: Room = {
      ...room,
      ...updates,
    }

    if (updates.children !== undefined) {
      const newChildAges = [...room.childAges]
      if (updates.children > room.children) {
        while (newChildAges.length < updates.children) {
          newChildAges.push(0)
        }
      } else if (updates.children < room.children) {
        newChildAges.splice(updates.children)
      }
      updatedRoom.childAges = newChildAges
    }

    updateRoom(index, updatedRoom)
  }, [rooms, updateRoom])

  // Yetişkin artır/azalt (maksimum 3 yetişkin; 3 yetişkin ise çocuk eklenemez)
  const handleAdultsChange = useCallback((index: number, delta: number) => {
    if (!rooms.length || index < 0 || index >= rooms.length) return

    const room = rooms[index]
    const newAdults = Math.max(1, Math.min(3, room.adults + delta))

    const updates: Partial<Room> = { adults: newAdults }
    if (newAdults === 3) {
      updates.children = 0
      updates.childAges = []
    }

    handleRoomUpdate(index, updates)
  }, [rooms, handleRoomUpdate])

  // Çocuk artır/azalt: oda max 3 kişi; max 1 çocuk (0-2 yaş varsa max 2 çocuk); 3 yetişkin ise çocuk yok
  const handleChildrenChange = useCallback((index: number, delta: number) => {
    if (!rooms.length || index < 0 || index >= rooms.length) return

    const room = rooms[index]
    if (room.adults >= 3) return // 3 yetişkin ise çocuk eklenemez

    const currentTotal = room.adults + room.children
    if (delta > 0 && currentTotal >= 3) return

    const hasInfantOrToddler = room.childAges.some((a) => a >= 0 && a <= 2)
    const maxChildren = hasInfantOrToddler ? 2 : 1
    const newChildren = Math.max(0, Math.min(maxChildren, room.children + delta))
    handleRoomUpdate(index, { children: newChildren })
  }, [rooms, handleRoomUpdate])

  // Çocuk yaşı değiştir; 2 çocuk varken birinin yaşı 0-2 dışına çıkarsa ikinci çocuk kaldırılır
  const handleChildAgeChange = useCallback((index: number, childIndex: number, age: number) => {
    if (!rooms.length || index < 0 || index >= rooms.length) return

    const room = rooms[index]
    if (childIndex < 0 || childIndex >= room.childAges.length) return

    const newChildAges = [...room.childAges]
    newChildAges[childIndex] = age

    if (room.children === 2) {
      const otherAge = newChildAges[childIndex === 0 ? 1 : 0]
      const thisIs02 = age >= 0 && age <= 2
      const otherIs02 = otherAge >= 0 && otherAge <= 2
      if (!thisIs02 && !otherIs02) {
        handleRoomUpdate(index, { children: 1, childAges: [newChildAges[childIndex]] })
        return
      }
    }
    handleRoomUpdate(index, { childAges: newChildAges })
  }, [rooms, handleRoomUpdate])

  // Çocuk yaşı için geçerli doğum tarihi aralığını hesapla
  const getChildBirthDateRange = useCallback((age: number): { start: Date; end: Date } | null => {
    if (!departureDate) return null

    const departure = new Date(departureDate)
    const endDate = new Date(departure)
    endDate.setFullYear(departure.getFullYear() - age)
    
    const startDate = new Date(endDate)
    startDate.setFullYear(startDate.getFullYear() - 1)
    startDate.setDate(startDate.getDate() + 1) // Bir gün sonrası

    return { start: startDate, end: endDate }
  }, [departureDate])

  if (!rooms.length) {
    return null
  }

  const roomListContent = (
    <>
            {/* Oda Listesi */}
            <div className="flex flex-col gap-4">
              {rooms.map((room, roomIndex) => {
                const totalPeople = room.adults + room.children
                const validation = validateRoomCapacity(room)
                const isAdultsMax = room.adults === 3

                return (
                  <div
                    key={`room-${roomIndex}`}
                    className={compactModal ? 'border border-gray-200 rounded-lg p-3 mb-2 last:mb-0' : 'border border-alsero-soft-white rounded-lg p-4'}
                  >
                    {/* Oda Başlığı ve Sil Butonu */}
                    <div className="flex flex-row justify-between items-center mb-4">
                      <h3 className="text-alsero-dark-gray font-semibold text-base">
                        {maxRooms === 1
                          ? getVariable('reservation.guest_party', 'Misafirler')
                          : getVariable('room_number', `${roomIndex + 1}. Oda`).replace(/\{number\}/g, String(roomIndex + 1))}
                      </h3>
                      {rooms.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            removeRoom(roomIndex)
                          }}
                          className="text-red-500 text-sm hover:text-red-700 transition-colors"
                        >
                          {getVariable('room_remove', 'Odayı Sil')}
                        </button>
                      )}
                    </div>

                    {/* Yetişkin ve Çocuk Sayısı - Yan Yana */}
                    <div className="mb-3 grid grid-cols-2 gap-3">
                      {/* Yetişkin Sayısı */}
                      <div>
                        <label className="text-alsero-dark-gray text-sm font-medium mb-2 block">
                          {getVariable('room_travelers_adults', 'Yetişkin')}
                        </label>
                        <div className="flex items-center bg-alsero-soft-white/50 rounded-lg p-2">
                          <button
                            type="button"
                            onClick={() => handleAdultsChange(roomIndex, -1)}
                            disabled={room.adults === 1}
                            aria-label="Decrease adult count"
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-tl-lg rounded-bl-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px] ${
                              room.adults === 1 
                                ? 'bg-alsero-light-gray/40 cursor-not-allowed' 
                                : 'bg-alsero-dark-gray hover:bg-alsero-dark-gray/90'
                            }`}
                          >
                            −
                          </button>
                          <input
                            type="text"
                            readOnly
                            value={room.adults}
                            aria-label={`Adult count: ${room.adults}`}
                            className="text-center h-8 w-12 sm:h-10 sm:w-16 text-alsero-dark-gray text-base sm:text-lg font-medium bg-alsero-soft-white/60"
                          />
                          <button
                            type="button"
                            onClick={() => handleAdultsChange(roomIndex, 1)}
                            disabled={room.adults === 3}
                            aria-label="Increase adult count"
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-tr-lg rounded-br-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px] ${
                              room.adults === 3 
                                ? 'bg-gray-300 cursor-not-allowed' 
                                : 'bg-alsero-dark-gray hover:bg-alsero-dark-gray/90'
                            }`}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Çocuk Sayısı */}
                      <div>
                        <label className="text-alsero-dark-gray text-sm font-medium mb-2 block">
                          {getVariable('room_travelers_children', 'Çocuk')}
                        </label>
                        <div className="flex items-center bg-alsero-soft-white/50 rounded-lg p-2">
                          <button
                            type="button"
                            onClick={() => handleChildrenChange(roomIndex, -1)}
                            disabled={room.children === 0 || isAdultsMax}
                            aria-label="Decrease children count"
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-tl-lg rounded-bl-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px] ${
                              room.children === 0 || isAdultsMax
                                ? 'bg-gray-300 cursor-not-allowed' 
                                : 'bg-alsero-dark-gray hover:bg-alsero-dark-gray/90'
                            }`}
                          >
                            −
                          </button>
                          <input
                            type="text"
                            readOnly
                            value={room.children}
                            aria-label={`Children count: ${room.children}`}
                            className={`text-center h-8 w-12 sm:h-10 sm:w-16 text-alsero-dark-gray text-base sm:text-lg font-medium bg-alsero-soft-white/60 ${
                              isAdultsMax ? 'opacity-50' : ''
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => handleChildrenChange(roomIndex, 1)}
                            disabled={isAdultsMax || totalPeople >= 3 || (room.children >= 2) || (room.children === 1 && !room.childAges.some((a) => a >= 0 && a <= 2))}
                            aria-label="Increase children count"
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-tr-lg rounded-br-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px] ${
                              isAdultsMax || totalPeople >= 3 || (room.children >= 2) || (room.children === 1 && !room.childAges.some((a) => a >= 0 && a <= 2))
                                ? 'bg-gray-300 cursor-not-allowed' 
                                : 'bg-blue-500 hover:bg-blue-600'
                            }`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Çocuk Yaşları */}
                    {room.children > 0 && (
                      <div className="mt-4 pt-4 border-t border-alsero-soft-white">
                        <label className="text-alsero-dark-gray text-sm font-medium mb-3 flex items-center gap-2">
                          {getVariable('room_travelers_child_age', getVariable('reservation.child_age_label', 'Çocuk Yaşı'))}
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-500" aria-hidden="true" focusable="false">
                            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                            <path d="M8 4.5V5M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </label>
                        <div className={`grid gap-2 sm:gap-3 ${room.childAges.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          {room.childAges.map((age, childIndex) => {
                            const birthDateRange = getChildBirthDateRange(age)
                            
                            return (
                              <div key={`child-age-${roomIndex}-${childIndex}`} className="flex flex-col">
                                <label className="sr-only">
                                  {getVariable('room_travelers_child_age', getVariable('reservation.child_age_label', 'Çocuk Yaşı'))} {childIndex + 1}
                                </label>
                                <select
                                  value={age}
                                  onChange={(e) => handleChildAgeChange(roomIndex, childIndex, parseInt(e.target.value))}
                                  disabled={isAdultsMax}
                                  className={`w-full border border-alsero-soft-white rounded-lg p-2 text-center text-alsero-dark-gray focus:outline-none focus:border-alsero-dark-gray min-h-[44px] ${
                                    isAdultsMax ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                >
                                  {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i} value={i}>
                                      {i} {getVariable('years_old', getVariable('reservation.years_old', 'yaş'))}
                                    </option>
                                  ))}
                                </select>
                                {birthDateRange && departureDate && (
                                  <span className="text-xs text-alsero-dark-gray/80 mt-0.5 block">
                                  {getVariable('reservation.child_age_birthdate_range_hint', 'Doğum tarihi {start} - {end} tarihleri arasında olmalıdır.')
                                    .replace('{start}', birthDateRange.start.toLocaleDateString('tr-TR'))
                                    .replace('{end}', birthDateRange.end.toLocaleDateString('tr-TR'))}
                                </span>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Validasyon Mesajları */}
                    {validation.warnings.length > 0 && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        {validation.warnings.map((warning, idx) => (
                          <p key={idx} className="text-red-600 text-xs">
                            {getVariable(warning, warning)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Oda Ekle Butonu */}
            {rooms.length < maxRooms && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  addRoom()
                }}
                className="mt-4 text-alsero-dark-gray text-sm font-medium hover:text-alsero-first transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {getVariable('room_add', '+ Oda Ekle')}
              </button>
            )}

            {/* Maksimum oda uyarısı (günlük tur tek grupta maxRooms=1 → gösterme) */}
            {maxRooms > 1 && rooms.length >= maxRooms && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-700 text-xs">
                  {getVariable('room_max_limit_warning', `Maksimum ${maxRooms} oda eklenebilir`).replace(/\{max\}/g, String(maxRooms))}
                </p>
              </div>
            )}
    </>
  )

  if (inline) {
    return (
      <div className="w-full">
        {title && (
          <label className="text-alsero-dark-gray text-sm font-medium mb-2 block">
            {title}
          </label>
        )}
        <div
          className={
            compactModal
              ? 'w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-alsero-dark-gray'
              : 'w-full px-4 py-3 bg-white border border-alsero-soft-white rounded-lg text-alsero-dark-gray'
          }
        >
          {roomListContent}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={dropdownRef}
      className="w-full relative z-[70]"
    >
      {title && (
        <label htmlFor="room-travelers-select" className="text-alsero-dark-gray text-base sm:text-lg mb-1 block">
          {title}
        </label>
      )}

      <button
        id="room-travelers-select"
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Select rooms and travelers. Currently: ${rooms.length} room(s), ${totalTravelers} traveler(s)`}
        className="relative flex flex-row justify-between items-center px-4 sm:px-6 py-3 bg-white border border-alsero-soft-white rounded-lg w-full min-h-[50px] sm:min-h-[60px] focus:outline-none cursor-pointer"
      >
        <div className="flex flex-row gap-2 items-center">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <span className="text-alsero-dark-gray text-base sm:text-lg">{summaryText}</span>
        </div>
        <svg
          width="15"
          height="8"
          viewBox="0 0 15 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
          focusable="false"
        >
          <path fillRule="evenodd" clipRule="evenodd" d="M14.3145 0.454593C14.6735 0.796556 14.6735 1.35099 14.3145 1.69295L8.18677 7.53063C7.82782 7.87259 7.24584 7.87259 6.88688 7.53063L0.75913 1.69295C0.400175 1.35099 0.400175 0.796555 0.75913 0.454592C1.11808 0.112629 1.70007 0.112629 2.05902 0.454592C5.12634 3.37671 9.94731 3.37671 13.0146 0.454593C13.3736 0.112629 13.9556 0.112629 14.3145 0.454593Z" fill="#2E2D2D"/>
        </svg>
      </button>

      {isOpen && (
        <div
          ref={dropdownContentRef}
          className="absolute top-full left-0 right-0 sm:left-auto sm:right-0 sm:w-[600px] w-full mt-2 z-[110] rounded-lg bg-white p-4 sm:p-6 shadow-lg flex flex-col max-h-[600px] overflow-y-auto"
        >
          {roomListContent}
        </div>
      )}
    </div>
  )
}
